import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { CreditCard, Wifi, Package, Loader2, AlertTriangle, XCircle, Clock, CheckCircle, ExternalLink, PauseCircle, Ban, Truck, Radio, MapPin, Plus, RefreshCw, ShoppingBag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusColors = { active: "text-emerald-400", past_due: "text-amber-400", suspended: "text-red-400", cancel_pending: "text-orange-400", canceled: "text-red-500", pending_payment: "text-[#A0A0A0]" };
const statusIcons = { active: CheckCircle, past_due: AlertTriangle, suspended: XCircle, cancel_pending: Clock, canceled: Ban, pending_payment: Clock };

const simMessages = {
  pending: { text: "Your SIM is being prepared", color: "text-[#D4AF37]", icon: Package },
  shipped: { text: "Your SIM is on the way", color: "text-blue-400", icon: Truck },
  delivered: { text: "SIM delivered. Activation in progress", color: "text-emerald-400", icon: CheckCircle },
  activation_pending: { text: "We are transferring your number", color: "text-amber-400", icon: Radio },
  active: { text: "Your service is fully active", color: "text-emerald-400", icon: CheckCircle },
};

const portMessages = {
  port_requested: "Port Requested",
  port_approved: "Port Approved",
  port_completed: "Port Completed",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [tab, setTab] = useState("overview");
  const [billingNotice, setBillingNotice] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  const [actionMsg, setActionMsg] = useState(null);

  const fetchData = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/dashboard`, { withCredentials: true }); setData(d); }
    catch { /* dashboard load failure handled by loading state */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMsg = (text, type = "success") => {
    setActionMsg({ text, type });
    setTimeout(() => setActionMsg(null), 5000);
  };

  const doAction = async (endpoint, confirm_msg) => {
    if (confirm_msg && !window.confirm(confirm_msg)) return;
    setActionLoading(endpoint);
    try {
      const { data: d } = await axios.post(`${API}/actions/${endpoint}`, {}, { withCredentials: true });
      if (d.url) { window.location.href = d.url; return; }
      showMsg(d.message || "Done"); fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || "Action failed", "error"); }
    finally { setActionLoading(""); }
  };

  const manageBilling = async () => {
    setActionLoading("billing");
    setBillingNotice(null);
    try {
      const { data: d } = await axios.post(`${API}/actions/manage-billing`, {}, { withCredentials: true });
      if (d.url) { window.location.href = d.url; return; }
      if (d.error === "no_customer" || d.error === "no_payment_method") {
        setBillingNotice(d.message);
        return;
      }
    } catch (err) {
      setBillingNotice("Unable to open billing portal. Please try again or add a payment method.");
    } finally { setActionLoading(""); }
  };

  const syncBilling = async () => {
    setSyncLoading(true);
    try {
      const { data: d } = await axios.post(`${API}/actions/sync-stripe`, {}, { withCredentials: true });
      if (d.rate_limited) return;
      if (d.synced) fetchData();
    } catch { /* sync failure is non-critical */ }
    finally { setSyncLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;

  const u = data?.user || user;
  const status = u?.status || "pending_payment";
  const StatusIcon = statusIcons[status] || Clock;
  const sub = data?.subscription;
  const sim = data?.sim;
  const payments = data?.payments || [];
  const requests = data?.requests || [];
  const pm = data?.payment_method;
  const hasPendingReq = (type) => requests.some(r => r.type === type && r.status === "pending");

  const simInfo = sim ? simMessages[sim.status] || simMessages.pending : null;
  const SimIcon = simInfo?.icon || Package;
  const isIncomplete = u?.role !== "admin" && (!u?.stripe_customer_id || !u?.stripe_subscription_id);

  const tabs = [
    { id: "overview", label: "Overview", icon: Wifi },
    { id: "service", label: "Service Status", icon: Radio },
    { id: "billing", label: "Billing", icon: CreditCard },
    { id: "purchases", label: "Purchases", icon: ShoppingBag },
  ];

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Status Banners */}
        {isIncomplete && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-5 mb-6" data-testid="banner-incomplete">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
              <div className="flex-1">
                <p className="text-[#D4AF37] font-manrope font-semibold text-sm">Complete your setup to activate service</p>
                <p className="text-[#D4AF37]/70 font-manrope text-xs mt-0.5">{!u?.stripe_customer_id ? "Your billing account needs to be set up." : "Your subscription is not yet active."} Complete the signup process to start your unlimited plan.</p>
              </div>
            </div>
            <button onClick={() => navigate("/signup")} className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="complete-setup-btn">
              <CreditCard className="w-4 h-4" /> Complete Signup & Add Payment
            </button>
          </div>
        )}
        {status === "past_due" && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="banner-past-due">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1"><p className="text-amber-400 font-manrope font-semibold text-sm">Payment failed. Service at risk.</p><p className="text-amber-400/70 font-manrope text-xs">Update your payment method to avoid service interruption.</p></div>
            <button onClick={manageBilling} className="bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-semibold flex-shrink-0" data-testid="banner-pay-now">Update Payment</button>
          </div>
        )}
        {status === "suspended" && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="banner-suspended">
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div className="flex-1"><p className="text-red-400 font-manrope font-semibold text-sm">Your service is suspended.</p><p className="text-red-400/70 font-manrope text-xs">Pay $25 reconnection fee to restore service.</p></div>
            <button onClick={() => doAction("restore-service")} disabled={actionLoading === "restore-service"} className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex-shrink-0" data-testid="banner-restore">
              {actionLoading === "restore-service" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Restore - $25"}
            </button>
          </div>
        )}
        {status === "cancel_pending" && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="banner-cancel-pending">
            <Clock className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <p className="text-orange-400 font-manrope text-sm">Your service will end at the end of your billing cycle{sub?.current_period_end ? ` (${new Date(sub.current_period_end).toLocaleDateString()})` : ""}.</p>
          </div>
        )}
        {/* SIM alert banners */}
        {sim && sim.status !== "active" && sim.status !== "pending" && (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="banner-sim">
            <SimIcon className={`w-5 h-5 ${simInfo?.color || "text-[#D4AF37]"} flex-shrink-0`} />
            <p className={`font-manrope text-sm ${simInfo?.color || "text-[#D4AF37]"}`}>{simInfo?.text}</p>
          </div>
        )}

        <h1 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-1" data-testid="dashboard-title">Welcome, {u?.name}</h1>
        <p className="text-[#A0A0A0] font-manrope mb-8">Manage your Prestigious Wireless account</p>

        <div className="flex gap-2 mb-8 border-b border-[#2A2A2A]" data-testid="dashboard-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-3 font-manrope text-sm font-medium border-b-2 -mb-[1px] transition-all ${tab === t.id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A0A0A0] hover:text-[#F5F5F5]"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Inline action feedback */}
        {actionMsg && (
          <div className={`rounded-xl p-3 mb-6 text-sm font-manrope ${actionMsg.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"}`} data-testid="action-message">{actionMsg.text}</div>
        )}

        {/* Overview Tab */}
        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="overview-tab">
            <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4"><h3 className="font-outfit text-lg font-semibold text-[#F5F5F5]">Plan Status</h3><StatusIcon className={`w-5 h-5 ${statusColors[status]}`} /></div>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Status</span><span className={`font-manrope font-semibold capitalize ${statusColors[status]}`} data-testid="user-status">{status.replace(/_/g, " ")}</span></div>
                <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Plan</span><span className="text-[#F5F5F5] font-manrope">Unlimited - $50/mo</span></div>
                {sub?.current_period_end && <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Next Billing</span><span className="text-[#F5F5F5] font-manrope text-sm">{new Date(sub.current_period_end).toLocaleDateString()}</span></div>}
              </div>
              <div className="mt-6 space-y-2">
                {status === "active" && !hasPendingReq("suspension") && (
                  <button onClick={() => doAction("request-suspension", "Request service suspension? Admin approval required.")} disabled={!!actionLoading} className="w-full border border-[#333] hover:border-amber-500 text-[#A0A0A0] hover:text-amber-400 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="request-suspension"><PauseCircle className="w-4 h-4" /> Request Suspension</button>
                )}
                {(status === "active" || status === "past_due") && !hasPendingReq("cancellation") && (
                  <button onClick={() => doAction("cancel-service", "Request service cancellation? This cannot be undone.")} disabled={!!actionLoading} className="w-full border border-[#333] hover:border-red-500 text-[#A0A0A0] hover:text-red-400 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="cancel-service"><Ban className="w-4 h-4" /> Cancel Service</button>
                )}
                {status === "suspended" && (
                  <button onClick={() => doAction("restore-service")} disabled={!!actionLoading} className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="restore-service">
                    {actionLoading === "restore-service" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Restore Service - $25"}
                  </button>
                )}
              </div>
            </div>
            <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-6">
              <h3 className="font-outfit text-lg font-semibold text-[#F5F5F5] mb-4">Account Info</h3>
              <div className="space-y-3">
                {[["Name", u?.name], ["Email", u?.email], ["Phone", u?.phone || "N/A"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">{k}</span><span className="text-[#F5F5F5] font-manrope text-sm">{v}</span></div>
                ))}
                {u?.address && <div className="flex justify-between"><span className="text-[#A0A0A0] font-manrope text-sm">Address</span><span className="text-[#F5F5F5] font-manrope text-sm text-right">{u.address.line1}, {u.address.city}</span></div>}
              </div>
              {/* Payment Method */}
              {pm && pm.type !== "none" && (
                <div className="mt-5 pt-5 border-t border-[#2A2A2A]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[#A0A0A0] font-manrope text-sm">Payment Method</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${pm.type === "ach" ? "bg-emerald-500/20 text-emerald-400" : "bg-[#D4AF37]/20 text-[#D4AF37]"}`} data-testid="payment-reliability">
                      {pm.reliability} Reliability
                    </span>
                  </div>
                  <p className="text-[#F5F5F5] font-manrope text-sm font-medium" data-testid="payment-method-label">{pm.label}</p>
                  {pm.type === "card" && (
                    <button onClick={manageBilling} className="mt-3 w-full bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-400 py-2 rounded-full text-xs font-semibold transition-all" data-testid="switch-to-bank">
                      Switch to Bank Account (Recommended)
                    </button>
                  )}
                </div>
              )}
              <button onClick={manageBilling} disabled={!!actionLoading} className="w-full mt-4 border border-[#333] hover:border-[#D4AF37] text-[#F5F5F5] py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="manage-billing">
                {actionLoading === "billing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4" /> Manage Billing & Payment</>}
              </button>
              {billingNotice && (
                <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="billing-notice">
                  <p className="text-amber-400 font-manrope text-sm mb-3">{billingNotice}</p>
                  <button onClick={() => navigate("/signup")} className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2" data-testid="add-payment-btn">
                    <Plus className="w-4 h-4" /> Add Payment Method
                  </button>
                </div>
              )}
              {!isIncomplete && (
                <button onClick={syncBilling} disabled={syncLoading} className="w-full mt-2 border border-[#2A2A2A] hover:border-[#333] text-[#A0A0A0] hover:text-[#F5F5F5] py-2 rounded-full text-xs font-manrope transition-all flex items-center justify-center gap-1.5" data-testid="sync-billing">
                  {syncLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Sync Billing
                </button>
              )}
            </div>
          </div>
        )}

        {/* Service Status Tab */}
        {tab === "service" && (
          <div data-testid="service-tab">
            {!sim ? (
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-12 text-center"><Package className="w-12 h-12 text-[#2A2A2A] mx-auto mb-4" /><p className="text-[#A0A0A0] font-manrope">No SIM order yet</p></div>
            ) : (
              <div className="space-y-6">
                {/* SIM Status Card */}
                <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-8">
                  <h3 className="font-outfit text-xl font-semibold text-[#F5F5F5] mb-6">SIM Card Status</h3>
                  {/* Status message banner */}
                  <div className={`bg-[#1A1A1A] rounded-xl p-4 mb-6 flex items-center gap-3 border border-[#2A2A2A]`}>
                    <SimIcon className={`w-6 h-6 ${simInfo?.color} flex-shrink-0`} />
                    <p className={`font-manrope font-semibold ${simInfo?.color}`} data-testid="sim-status-message">{simInfo?.text}</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                      <span className="text-[#A0A0A0] font-manrope">SIM Number</span>
                      <span className="text-[#D4AF37] font-manrope font-mono" data-testid="sim-number">{sim.sim_number}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                      <span className="text-[#A0A0A0] font-manrope">Order ID</span>
                      <span className="text-[#F5F5F5] font-manrope font-mono text-sm" data-testid="sim-order-id">{sim.order_id || "N/A"}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                      <span className="text-[#A0A0A0] font-manrope">Status</span>
                      <span className={`font-manrope font-semibold capitalize ${simInfo?.color}`} data-testid="sim-status">{sim.status?.replace(/_/g, " ")}</span>
                    </div>
                    {sim.tracking_number && (
                      <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                        <span className="text-[#A0A0A0] font-manrope">Tracking Number</span>
                        <span className="text-[#F5F5F5] font-manrope font-mono" data-testid="sim-tracking">{sim.tracking_number}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-[#A0A0A0] font-manrope">Ordered</span>
                      <span className="text-[#F5F5F5] font-manrope text-sm">{new Date(sim.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Fulfillment Timeline */}
                  <div className="mt-8">
                    <h4 className="text-[#A0A0A0] text-xs font-manrope uppercase tracking-wider mb-4">Fulfillment Progress</h4>
                    <div className="flex items-center gap-0">
                      {["pending", "shipped", "delivered", "activation_pending", "active"].map((s, i) => {
                        const stages = ["pending", "shipped", "delivered", "activation_pending", "active"];
                        const currentIdx = stages.indexOf(sim.status);
                        const done = i <= currentIdx;
                        return (
                          <div key={s} className="flex-1 flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${done ? "bg-[#D4AF37] text-black" : "bg-[#2A2A2A] text-[#A0A0A0]"}`}>{i + 1}</div>
                            {i < 4 && <div className={`flex-1 h-0.5 ${i < currentIdx ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"}`} />}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex mt-2">
                      {["Pending", "Shipped", "Delivered", "Activating", "Active"].map((s, i) => (
                        <div key={i} className="flex-1 text-center text-[9px] text-[#A0A0A0] font-manrope">{s}</div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Port Status */}
                {sim.porting_required && (
                  <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-8" data-testid="port-status-card">
                    <h3 className="font-outfit text-xl font-semibold text-[#F5F5F5] mb-6">Number Port Status</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                        <span className="text-[#A0A0A0] font-manrope">Number</span>
                        <span className="text-[#F5F5F5] font-manrope" data-testid="port-number">{sim.port_number || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-[#2A2A2A]">
                        <span className="text-[#A0A0A0] font-manrope">From Carrier</span>
                        <span className="text-[#F5F5F5] font-manrope" data-testid="port-carrier">{sim.port_carrier || "N/A"}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-[#A0A0A0] font-manrope">Port Status</span>
                        <span className={`font-manrope font-semibold ${sim.port_status === "port_completed" ? "text-emerald-400" : "text-[#D4AF37]"}`} data-testid="port-status">
                          {portMessages[sim.port_status] || sim.port_status || "Pending"}
                        </span>
                      </div>
                    </div>
                    {/* Port Timeline */}
                    <div className="mt-6 flex items-center gap-0">
                      {["port_requested", "port_approved", "port_completed"].map((s, i) => {
                        const stages = ["port_requested", "port_approved", "port_completed"];
                        const currentIdx = stages.indexOf(sim.port_status);
                        const done = i <= currentIdx;
                        return (
                          <div key={s} className="flex-1 flex items-center">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done ? "bg-[#D4AF37] text-black" : "bg-[#2A2A2A] text-[#A0A0A0]"}`}>{i + 1}</div>
                            {i < 2 && <div className={`flex-1 h-0.5 ${i < currentIdx ? "bg-[#D4AF37]" : "bg-[#2A2A2A]"}`} />}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex mt-2">
                      {["Requested", "Approved", "Completed"].map((s, i) => (
                        <div key={i} className="flex-1 text-center text-[9px] text-[#A0A0A0] font-manrope">{s}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Billing Tab */}
        {tab === "billing" && (
          <div data-testid="billing-tab">
            <h3 className="font-outfit text-xl font-semibold text-[#F5F5F5] mb-6">Payment History</h3>
            {payments.length === 0 ? (
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-12 text-center"><CreditCard className="w-12 h-12 text-[#2A2A2A] mx-auto mb-4" /><p className="text-[#A0A0A0] font-manrope">No payments yet</p></div>
            ) : (
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl overflow-hidden">
                <table className="w-full" data-testid="payments-table">
                  <thead><tr className="border-b border-[#2A2A2A]">{["Date", "Type", "Amount", "Status"].map(h => <th key={h} className="text-left text-[#A0A0A0] text-xs font-manrope uppercase tracking-wider p-4">{h}</th>)}</tr></thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={i} className="border-b border-[#2A2A2A] last:border-0 hover:bg-[#1A1A1A]">
                        <td className="p-4 text-[#F5F5F5] font-manrope text-sm">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="p-4 text-[#A0A0A0] font-manrope text-sm capitalize">{p.type}</td>
                        <td className="p-4 text-[#D4AF37] font-manrope font-semibold">${p.amount?.toFixed(2)}</td>
                        <td className="p-4"><span className={`text-sm font-semibold capitalize ${p.payment_status === "paid" ? "text-emerald-400" : "text-[#D4AF37]"}`}>{p.payment_status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Purchases Tab */}
        {tab === "purchases" && (
          <div data-testid="purchases-tab">
            <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-6">
              <h2 className="font-outfit text-lg font-semibold text-[#F5F5F5] mb-4 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#D4AF37]" /> Purchase History</h2>
              {data?.store_orders?.length > 0 ? (
                <div className="space-y-3">
                  {data.store_orders.map((o, idx) => (
                    <div key={`${o.created_at}-${idx}`} className="flex items-center justify-between py-3 border-b border-[#2A2A2A] last:border-0">
                      <div>
                        <p className="text-[#F5F5F5] font-manrope text-sm font-medium">{o.items ? o.items.map(i => `${i.name}${i.quantity > 1 ? ` x${i.quantity}` : ""}`).join(", ") : o.product_name}</p>
                        <p className="text-[#A0A0A0] text-xs font-manrope">{new Date(o.created_at).toLocaleDateString()}{o.payment_method && o.payment_method !== "stripe" ? ` \u00B7 ${o.payment_method}` : ""}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-outfit font-semibold">${(o.total_amount || o.price || 0).toFixed(2)}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${o.status === "fulfilled" ? "bg-emerald-500/20 text-emerald-400" : o.status === "refunded" ? "bg-red-500/20 text-red-400" : "bg-[#D4AF37]/20 text-[#D4AF37]"}`}>{o.fulfillment_status || o.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Package className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
                  <p className="text-[#A0A0A0] font-manrope text-sm">No purchases yet.</p>
                  <a href="/store" className="text-[#D4AF37] font-manrope text-sm hover:underline mt-1 inline-block">Visit the Store</a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
